import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { EmployeeService } from './employee.service';
import { Service } from '../models/provider-user-info.model';
import { environment } from '../../environments/environment';
import { AdminUserType, ProviderData, SubService } from '@k12/models';
import { Observable, switchMap, map, catchError, of } from 'rxjs';
import { IUserRecordProviderDetailsService } from '@k12/admin-services';
import { Address } from '@k12/models';
import { AppInsightsService } from '@k12/error_handler';

@Injectable({
  providedIn: 'root'
})
export class ProviderRecordService implements IUserRecordProviderDetailsService {
  private readonly http: HttpClient = inject(HttpClient);
  private readonly employeeService: EmployeeService = inject(EmployeeService);
  private readonly appInsightsService = inject(AppInsightsService);
  public subServices: Service[] = [];

  public getProviderDetails(userId: string, userType: AdminUserType = AdminUserType.Provider): Observable<unknown> {
    if (userType === AdminUserType.Provider) {
      return this.http.get<ProviderData>(`${environment.adminApiUrl}/partners/providers/${userId}`).pipe(
        switchMap((userData: ProviderData | null) => {

          // Handle null or undefined userData
          if (!userData) {
            return of(null);
          }

          // Find the owner employee
          const ownerEmployee = userData.employees?.find((emp) => emp.isOwner === true);

          if (ownerEmployee) {
            // Fetch detailed owner information
            return this.employeeService.GetEmployeeDetails(String(ownerEmployee.id)).pipe(
              map((ownerDetails) => ({
                ...userData,
                ownerDetails: ownerDetails
              })),
              catchError((error: unknown) => {
                this.appInsightsService.log(error instanceof Error ? error : new Error(String(error)));
                return of(userData); // Return original data if owner details fail
              })
            );
          } else {
            // No owner found, return original user data
            return of(userData);
          }
        }),
        catchError((error: unknown) => {
          this.appInsightsService.log(error instanceof Error ? error : new Error(String(error)));
          return of(null);
        })
      );
    }
  
    return of(null);
  }

  parseProviderAddress(address: Address | null | undefined): string {
    if (!address) {
      return '';
    }

    let addressString = '';

    if (address.address1) {
      addressString += address.address1;
    }

    if (address.address2) {
      addressString += addressString ? '\n' + address.address2 : address.address2;
    }

    const cityStateZip: string[] = [];
    if (address.city) cityStateZip.push(address.city);
    let stateStr = '';
    if (address.state != null) {
      stateStr = typeof address.state === 'string' ? address.state : address.state.name;
    }
    if (address.county) cityStateZip.push(address.county);
    if (stateStr) cityStateZip.push(stateStr);
    if (address.zip) cityStateZip.push(address.zip);

    if (cityStateZip.length > 0) {
      addressString += addressString ? '\n' + cityStateZip.join(', ') : cityStateZip.join(', ');
    }

    return addressString.replace(/\n/g, '<br>');
  }

  parseServices(services: SubService[]): string[] {
    if (!services) {
      return [];
    }

    return services.map((service) => service.name ?? '');
  }

  getSubServices(employeeType?: number): Observable<SubService[]> {
    if (employeeType) {
      return this.http.get<SubService[]>(
        `${environment.adminApiUrl}/partners/employees/subservices?employeeType=${employeeType}`
      );
    } else {
      return this.http.get<SubService[]>(`${environment.adminApiUrl}/partners/employees/subservices`);
    }
  }

  getAllProviders(): Observable<ProviderData[]> {
    return this.http.get<ProviderData[]>(`${environment.adminApiUrl}/partners/providers`);
  }

  async editProviderDetails(payload: ProviderData): Promise<Observable<unknown>> {
    return this.http.put(`${environment.adminApiUrl}/partners/providers`, payload);
  }

  getProviderContacts(userId: string): Observable<unknown> {
    return this.http.get(`${environment.adminApiUrl}/schools/${userId}/hdmaContacts`);
  }
}
